import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  ArrowRight,
  Loader2,
  Search,
  Filter,
  Plus,
  X,
} from "lucide-react";
import { COMPLIANCE_TYPES, STATUSES } from "@/components/ComplianceEditor";

const TYPE_LABEL = {
  background_check: "Background check",
  vulnerable_sector_check: "Vulnerable sector",
  criminal_record: "Criminal record",
  work_eligibility: "Work eligibility",
  education_verification: "Education",
  references: "References",
  tb_test: "TB test",
  immunizations: "Immunizations",
  vaccinations: "Vaccinations",
  cpr: "CPR",
  first_aid: "First Aid",
  nursing_license: "Nursing license",
  professional_license: "License",
  drivers_license: "Driver's license",
  insurance: "Insurance",
  other: "Other",
};

const STATUS_FILTERS = [
  { key: "all", label: "All flagged" },
  { key: "expired", label: "Expired" },
  { key: "expiring_soon", label: "Expiring ≤ 30d" },
  { key: "rejected", label: "Rejected" },
];

function StatusPill({ status }) {
  if (status === "expired") {
    return <span className="pill bg-destructive/10 text-destructive">Expired</span>;
  }
  if (status === "rejected") {
    return <span className="pill bg-destructive/10 text-destructive">Rejected</span>;
  }
  if (status === "expiring_soon") {
    return <span className="pill bg-amber-100 text-amber-800">Expiring soon</span>;
  }
  return <span className="pill bg-clinical-bg text-clinical-subtext capitalize">{status}</span>;
}

function CounterTile({ label, value, sublabel, icon: Icon, tone, testid }) {
  const tones = {
    primary: "border-clinical-border bg-white text-clinical-text",
    danger: "border-destructive/20 bg-destructive/5 text-destructive",
    warn: "border-amber-300/40 bg-amber-50 text-amber-800",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };
  return (
    <div
      className={`rounded-xl border p-4 ${tones[tone] || tones.primary}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <div className="label-eyebrow text-current">{label}</div>
      </div>
      <div className="mt-1 font-heading text-2xl font-semibold text-current">{value}</div>
      {sublabel ? (
        <div className="text-[11px] text-clinical-subtext mt-0.5">{sublabel}</div>
      ) : null}
    </div>
  );
}

export default function AdminCompliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  const [users, setUsers] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/compliance-summary"),
      api.get("/auth/users"),
    ])
      .then(([cs, us]) => {
        setData(cs.data);
        setUsers((us.data || []).filter((u) => u.active !== false));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    if (!data?.flagged) return [];
    const q = query.trim().toLowerCase();
    return data.flagged.filter((f) => {
      if (statusFilter !== "all" && f.status !== statusFilter) return false;
      if (!q) return true;
      const label = (TYPE_LABEL[f.type] || f.label || "").toLowerCase();
      return (
        f.name.toLowerCase().includes(q) ||
        f.email.toLowerCase().includes(q) ||
        label.includes(q)
      );
    });
  }, [data, statusFilter, query]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading compliance…
      </div>
    );
  }

  const counters = data.counters || {};
  const total = data.total_caregivers || 0;
  const verified = data.fully_verified_caregivers || 0;
  const percent = total > 0 ? Math.round((verified / total) * 100) : 0;

  const counts = {
    all: data.flagged.length,
    expired: data.flagged.filter((f) => f.status === "expired").length,
    expiring_soon: data.flagged.filter((f) => f.status === "expiring_soon").length,
    rejected: data.flagged.filter((f) => f.status === "rejected").length,
  };

  return (
    <div className="space-y-8" data-testid="admin-compliance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Compliance</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            Caregiver compliance
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Track background checks, certifications, vaccinations, and licenses across the team.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddItem(true)}
          className="btn-primary"
          data-testid="add-compliance-item-button"
        >
          <Plus className="h-4 w-4" /> Add compliance item
        </button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div
          className="rounded-xl border border-clinical-border bg-clinical-bg p-4"
          data-testid="compliance-page-ready-stat"
        >
          <div className="label-eyebrow">Fully verified</div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="font-heading text-2xl font-semibold text-clinical-text">
              {verified}
            </span>
            <span className="text-sm text-clinical-subtext">/ {total}</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
            <div
              className="h-full rounded-full bg-clinical-primary"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="mt-1.5 text-[11px] text-clinical-subtext">
            {percent}% audit-ready
          </div>
        </div>

        <CounterTile
          label="Expired"
          value={counters.expired || 0}
          sublabel="Block from new shifts"
          icon={ShieldX}
          tone="danger"
          testid="compliance-page-expired-stat"
        />
        <CounterTile
          label="Expiring ≤ 30d"
          value={counters.expiring_soon || 0}
          sublabel="Renew before they lapse"
          icon={ShieldAlert}
          tone="warn"
          testid="compliance-page-expiring-stat"
        />
        <CounterTile
          label="Pending review"
          value={(counters.submitted || 0) + (counters.pending || 0)}
          sublabel={`${counters.submitted || 0} submitted · ${counters.pending || 0} not started`}
          icon={ShieldCheck}
          tone="primary"
          testid="compliance-page-pending-stat"
        />
      </div>

      {/* Filters + search */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-clinical-subtext" />
            <h2 className="font-heading text-lg font-medium">Items needing attention</h2>
          </div>
          <Link
            to="/admin/team"
            className="text-xs text-clinical-primary hover:underline inline-flex items-center gap-1"
            data-testid="compliance-page-team-link"
          >
            Open Team page <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div
            className="flex flex-wrap gap-1.5 bg-clinical-bg p-1 rounded-lg"
            data-testid="compliance-status-filter"
          >
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatusFilter(s.key)}
                data-testid={`compliance-filter-${s.key}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  statusFilter === s.key
                    ? "bg-white text-clinical-text shadow-sm"
                    : "text-clinical-subtext hover:text-clinical-text"
                }`}
              >
                {s.label}
                <span className="ml-1.5 text-[10px] text-clinical-subtext">
                  {counts[s.key]}
                </span>
              </button>
            ))}
          </div>

          <div className="relative flex-1 md:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-clinical-subtext" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search caregiver or item…"
              data-testid="compliance-search-input"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-clinical-border text-sm focus:outline-none focus:ring-2 focus:ring-clinical-primary/30 focus:border-clinical-primary"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-800 flex items-center gap-2"
            data-testid="compliance-page-empty"
          >
            <ShieldCheck className="h-4 w-4 shrink-0" />
            {data.flagged.length === 0
              ? "All caregivers are compliant — nothing needs attention right now."
              : "No items match the current filter."}
          </div>
        ) : (
          <ul className="divide-y divide-clinical-border" data-testid="compliance-items-list">
            {filtered.map((f) => {
              const label = TYPE_LABEL[f.type] || f.label || "Compliance item";
              const dayText =
                f.status === "expired"
                  ? f.expires_on
                    ? `Expired ${f.expires_on}`
                    : "Expired"
                  : f.status === "rejected"
                  ? "Rejected — re-upload needed"
                  : f.days_left != null && f.days_left >= 0
                  ? `${f.days_left} day${f.days_left === 1 ? "" : "s"} left · expires ${f.expires_on || "—"}`
                  : "Expiring soon";
              return (
                <li
                  key={`${f.user_id}-${f.item_id}`}
                  className="py-3 flex items-start gap-3"
                  data-testid={`compliance-item-${f.user_id}-${f.item_id}`}
                >
                  <span
                    className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg ${
                      f.status === "expired" || f.status === "rejected"
                        ? "bg-destructive/10 text-destructive"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    <ShieldAlert className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-clinical-text truncate">
                        {f.name}
                      </span>
                      <span className="text-xs text-clinical-subtext">·</span>
                      <span className="text-sm text-clinical-text">{label}</span>
                      <StatusPill status={f.status} />
                    </div>
                    <div className="text-[11px] text-clinical-subtext mt-0.5">
                      {f.email} · {dayText}
                    </div>
                  </div>
                  <Link
                    to={`/admin/team?user=${f.user_id}`}
                    className="pill bg-clinical-bg text-clinical-text hover:bg-clinical-border/60 transition-colors shrink-0"
                    data-testid={`compliance-resolve-${f.user_id}-${f.item_id}`}
                  >
                    Review
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer breakdown */}
      <div className="card-surface p-5" data-testid="compliance-breakdown">
        <h2 className="font-heading text-lg font-medium mb-3">Status breakdown</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
          {[
            ["verified", "Verified", "text-emerald-700"],
            ["expiring_soon", "Expiring soon", "text-amber-700"],
            ["expired", "Expired", "text-destructive"],
            ["rejected", "Rejected", "text-destructive"],
            ["submitted", "Submitted", "text-clinical-primary"],
            ["pending", "Pending", "text-clinical-subtext"],
          ].map(([k, label, cls]) => (
            <div
              key={k}
              className="rounded-lg border border-clinical-border p-3"
              data-testid={`compliance-breakdown-${k}`}
            >
              <div className="label-eyebrow">{label}</div>
              <div className={`mt-1 font-heading text-xl font-semibold ${cls}`}>
                {counters[k] || 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showAddItem && (
        <AddComplianceItemModal
          users={users}
          onClose={() => setShowAddItem(false)}
          onSaved={() => {
            setShowAddItem(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function AddComplianceItemModal({ users, onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [type, setType] = useState("background_check");
  const [label, setLabel] = useState("");
  const [status, setStatus] = useState("verified");
  const [issuedOn, setIssuedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const caregivers = useMemo(
    () => users.filter((u) => u.role === "caregiver"),
    [users]
  );

  const save = async () => {
    if (!userId) {
      toast.error("Pick a caregiver");
      return;
    }
    if (type === "other" && !label.trim()) {
      toast.error("Add a label for 'Other'");
      return;
    }
    setSaving(true);
    try {
      // Fetch latest user payload to avoid stale overwrite
      const fresh = users.find((u) => u.id === userId);
      const existingItems = fresh?.compliance_items || [];
      const newItem = {
        type,
        label: type === "other" ? label.trim() : "",
        status,
        issued_on: issuedOn || "",
        expires_on: expiresOn || "",
        reference: reference.trim(),
        note: note.trim(),
      };
      const payload = {
        compliance_items: [...existingItems, newItem],
      };
      await api.put(`/auth/users/${userId}`, payload);
      toast.success("Compliance item added");
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not add compliance item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="add-compliance-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-lg font-medium">Add compliance item</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-ghost p-1.5"
            data-testid="add-compliance-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-eyebrow mb-1.5 block">Caregiver</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="input-clinical"
              data-testid="add-compliance-caregiver-select"
            >
              <option value="">Select a caregiver…</option>
              {caregivers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} — {u.email}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="input-clinical"
                data-testid="add-compliance-type-select"
              >
                {COMPLIANCE_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="input-clinical"
                data-testid="add-compliance-status-select"
              >
                {STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {type === "other" && (
            <div>
              <label className="label-eyebrow mb-1.5 block">Custom label</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. State agency check"
                className="input-clinical"
                data-testid="add-compliance-label-input"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-clinical-subtext block mb-1">
                Issued on
              </label>
              <input
                type="date"
                value={issuedOn}
                onChange={(e) => setIssuedOn(e.target.value)}
                className="input-clinical"
                data-testid="add-compliance-issued-input"
              />
            </div>
            <div>
              <label className="text-xs text-clinical-subtext block mb-1">
                Expires on
              </label>
              <input
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
                className="input-clinical"
                data-testid="add-compliance-expires-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-clinical-subtext block mb-1">
              Reference / vendor / file
            </label>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Sterling check #12345"
              className="input-clinical"
              data-testid="add-compliance-reference-input"
            />
          </div>

          <div>
            <label className="text-xs text-clinical-subtext block mb-1">
              Admin note (internal)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="input-clinical resize-y"
              data-testid="add-compliance-note-input"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary"
            data-testid="add-compliance-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !userId}
            className="btn-primary"
            data-testid="add-compliance-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add item"}
          </button>
        </div>
      </div>
    </div>
  );
}
